import { colors } from '@ecosystem/newron-design-system';
import { ExportExploreTrendsStack } from './Types';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { PageType, ShareType } from '@utils/pageConfig/Types';
import Highcharts, { Chart } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { chartOption } from '@data-visualization/utilities/CommonOptions';
import { NoResult } from '@pages/NoResult';
import { convertToSvgImage } from '@utils/SvgToPng';
import {
  createAxisLabelFormatter,
  createDataLabelFormatter,
  createTooltipFormatter,
  createTooltipPositioner,
  exportChartKey,
  formatLabel,
  getKpiData,
  mapAndSortChartData,
  renderCustomLabels,
  renderSegmentHeaderRow,
  renderTruncatingOverlay,
  syncLegendClick,
  syncLegendHover,
} from '@data-visualization/trends-stack-chart/StackedChartUtilities';
import { ChartKeys } from '@const/ChartKeys';
import { enableChartsExporting } from '@ecosystem/export-utils';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { SortFieldDisplayProps } from '@components/design/migrated/Table';
import { KpiTypeTrendStackChart } from '@pages/explore/ExploreContants';
import { ColumnHeaderForCL } from '@models/CategoryLeaderboard';
import { getDisplayNameFromHeaderKey } from '@models/PerformanceAnalysisboard';

interface Props {
  data: ExportExploreTrendsStack;
  selectedGrowthType?: GrowthTypes;
  noResultHandler?: () => void;
  noPeriodExistForMulticountry?: boolean;
  selectedSaleType?: KpiTypeTrendStackChart;
  selectedAbsoluteType?: ShareType;
  isMarket?: boolean;
  stackedChartSortingOrder?: SortFieldDisplayProps;
  volume?: string;
  isSegment?: boolean;
}

function getStackTotals(categories: string[], chartData: any[], dataKey: 'data1' | 'data2') {
  return categories.map((_, catIndex) => chartData.reduce((sum, series) => sum + (series[dataKey][catIndex] ?? 0), 0));
}

const ExploreTrendsStackChart: React.FC<Props> = ({
  data,
  selectedGrowthType = GrowthTypes.percentage,
  noResultHandler,
  noPeriodExistForMulticountry,
  selectedSaleType,
  selectedAbsoluteType,
  isMarket,
  stackedChartSortingOrder,
  volume,
  isSegment = false,
}) => {
  enableChartsExporting();
  const isEmptyData = noPeriodExistForMulticountry || !data?.salesRecordsFound; //NOSONAR typescript:S6606
  const { getBrandColorMap } = colors.useChartColors();

  const { handleExportImage } = useExportImages(PageType.EXPLORE);
  const chartRef1 = useRef<Chart | null>(null);
  const chartRef2 = useRef<Chart | null>(null);
  const exportChartRef = useRef<Chart | null>(null); // Reference for the hidden export chart
  const chartComponent1 = useRef<HighchartsReact.RefObject>(null);

  const chartComponent2 = useRef<HighchartsReact.RefObject>(null);
  const chartComponent3 = useRef<HighchartsReact.RefObject>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedChartData = getKpiData(data.series, selectedAbsoluteType, selectedSaleType, isMarket, isSegment);

  const chartData = mapAndSortChartData(selectedChartData, stackedChartSortingOrder, getBrandColorMap);

  const labelOffset1 = 0;
  const labelOffset2 = !isEmptyData ? data?.categories?.slice(0, 2).length : 0;

  const formatLabelWrapper = (offset: number) => (index: number) =>
    formatLabel(index + offset, data, isMarket, selectedGrowthType, selectedSaleType, isSegment);

  // Attach customData so that the render event can access our formatLabel function.
  const attachCustomData = (chart: Chart, labelOffset: number) => {
    chart.customData = {
      data,
      isMarket,
      formatLabel: formatLabelWrapper(labelOffset),
    };
  };

  const handleLegendHover = (e: any) => {
    if (!chartRef1.current || !chartRef2.current) return;
    syncLegendHover([chartRef1.current, chartRef2.current], e);
  };

  const handleLegendClick = (e: any) => {
    if (!chartRef1.current || !chartRef2.current) return;
    syncLegendClick([chartRef1.current, chartRef2.current], e);
  };

  const memoizedOptions = useMemo(() => {
    if (isEmptyData) return () => ({});

    const tooltipPositioner = createTooltipPositioner();
    const tooltipFormatter = createTooltipFormatter(selectedAbsoluteType);
    const dataLabelFormatter = createDataLabelFormatter(selectedAbsoluteType);
    const axisLabelFormatter = createAxisLabelFormatter();
    const trendsChartTopLabelText = [ColumnHeaderForCL.marketVolume, ColumnHeaderForCL.yourVolume].includes(
      selectedSaleType,
    )
      ? 'Units'
      : 'Revenue';
    const segmentChartTopLabelText = getDisplayNameFromHeaderKey(selectedSaleType, volume);

    // Returns the options function
    function createOptions(categories: string[], dataKey: 'data1' | 'data2', hideLegend = false, labelOffset = 0) {
      const isData1 = dataKey === 'data1';
      const chartExtra = isData1 ? { width: 430 } : { width: 90 * categories.length };
      const chartMarginLeft = isData1 ? 230 : 0;
      let yAxisOptions: any = {
        type: 'linear',
        endOnTick: false,
        startOnTick: false,
        gridLineWidth: 0,
        labels: { enabled: false },
        title: { text: null },
      };
      if (selectedAbsoluteType === ShareType.Absolute) {
        const stackTotals = getStackTotals(categories, chartData, dataKey);
        yAxisOptions.max = Math.max(...stackTotals);
      }
      return {
        chart: {
          type: 'column',
          height: isSegment ? 650 : 600,
          marginTop: isSegment ? 124 : 70,
          marginBottom: isSegment ? 50 : 100,
          marginLeft: chartMarginLeft,
          ...chartExtra,
          events: {
            render() {
              renderCustomLabels(this, dataKey, isSegment);
              renderSegmentHeaderRow(this, dataKey, {
                ...(dataKey === 'data1' ? { firstChartSpan: 2, xOffset: 230 } : {}),
                yOffset: 30,
                align: 'center',
              });

              if (dataKey === 'data1') {
                const trendsLines = [`${trendsChartTopLabelText} Change`, `${trendsChartTopLabelText} in Millions`];
                const segmentsLines = [
                  `${segmentChartTopLabelText} Change`,
                  `${segmentChartTopLabelText} in Millions`,
                  'Segment Share',
                ];
                renderTruncatingOverlay(this, isSegment ? segmentsLines : trendsLines, {
                  yOffset: isSegment ? 60 : 40,
                  lineHeight: 22,
                  paddingX: 0,
                });
              }
            },
          },

          customData: {
            data,
            isMarket,
            formatLabel: (index: number) => formatLabel(index + labelOffset, data, isMarket),
          },
        },
        xAxis: {
          type: 'category',
          categories,
          lineColor: 'transparent',
          crosshair: false,
          labels: {
            useHTML: true,
            formatter: axisLabelFormatter,
            y: 30,
            style: {
              fontSize: '12px',
              fontFamily: 'Lato, sans-serif',
              fontWeight: 500,
              color: '#6f7582',
            },
          },
        },
        yAxis: yAxisOptions,
        scrollbar: { enabled: true },
        title: chartOption.title,
        credits: chartOption.credits,
        tooltip: {
          shared: true,
          useHTML: true,
          backgroundColor: 'transparent',
          borderWidth: 0,
          shadow: false,
          animation: false,
          style: {
            color: '#333',
            fontSize: '14px',
            fontFamily: 'Lato, sans-serif',
            zIndex: 99999,
          },
          hideDelay: 0,
          positioner: tooltipPositioner,
          formatter: tooltipFormatter,
        },
        plotOptions: {
          series: {
            events: { legendItemClick: handleLegendClick },
            animation: false,
          },
          column: {
            stacking: selectedAbsoluteType === ShareType.Absolute ? 'normal' : 'percent',
            minPointLength: 0,
            grouping: false,
            groupPadding: 0,
            pointPadding: 0,
            borderWidth: 0,
            pointWidth: 80,
            dataLabels: {
              enabled: true,
              formatter: dataLabelFormatter,
              useHTML: true,
            },
          },
        },
        legend: {
          enabled: !hideLegend,
          layout: 'vertical',
          align: 'left',
          verticalAlign: 'top',
          x: -20,
          y: isSegment ? 170 : 100,
          itemMarginBottom: 8,
          itemStyle: {
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: 'Lato, sans-serif',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: '#414752',
            overflow: 'hidden',
            width: 200,
          },
          symbolHeight: 12,
          symbolWidth: 12,
          symbolRadius: 2,
        },
        series: chartData.map((series) => ({
          name: series.name,
          data: series[dataKey],
          color: series.color,
          animation: false,
        })),
      };
    }

    return createOptions;
  }, [data, selectedAbsoluteType, selectedSaleType, isMarket, getBrandColorMap, stackedChartSortingOrder]);

  function attachLegendHoverEvents(chart: Chart, handleLegendHover: (e: any) => void) {
    if (chart?.legend?.allItems) {
      chart.legend.allItems.forEach((item, index) => {
        if (item?.legendItem?.element) {
          Highcharts.addEvent(item.legendItem.element, 'mouseover', () =>
            handleLegendHover({ target: { index }, type: 'hover' }),
          );
          Highcharts.addEvent(item.legendItem.element, 'mouseout', () =>
            handleLegendHover({ target: { index }, type: '' }),
          );
        }
      });
    }
  }

  const callback1: Highcharts.ChartCallbackFunction = useCallback((chart) => {
    chartRef1.current = chart;
    attachCustomData(chart, labelOffset1);
    attachLegendHoverEvents(chart, handleLegendHover);
    Highcharts.addEvent(chart, 'redraw', () => attachLegendHoverEvents(chart, handleLegendHover));
  }, []);

  const callback2: Highcharts.ChartCallbackFunction = useCallback((chart) => {
    chartRef2.current = chart;
    attachCustomData(chart, labelOffset2);
  }, []);

  const callback3: Highcharts.ChartCallbackFunction = useCallback((chart) => {
    exportChartRef.current = chart;
    attachCustomData(chart, labelOffset2);
  }, []);

  useEffect(() => {
    if (isEmptyData || !chartRef1.current || !exportChartRef.current) return;
    const chart1 = chartRef1.current;
    const chart2 = exportChartRef.current;
    if (typeof chart1.getSVG !== 'function' || typeof chart2.getSVG !== 'function') return;

    const updateSvgAndExportImage = async () => {
      // Get SVG for chart1 (with added text)
      let svg1: string, svg2: string;
      try {
        svg1 = chart1.getSVG({
          exporting: {
            sourceWidth: chart1.chartWidth,
            sourceHeight: chart1.chartHeight,
            scale: 2,
          },
        });
        svg2 = chart2.getSVG({
          exporting: {
            sourceWidth: chart2.chartWidth,
            sourceHeight: chart2.chartHeight,
            scale: 2,
          },
        });
      } catch (error) {
        console.error('Error generating SVG:', error);
        return;
      }

      const addedText1 = chart1.container.querySelector('.stack-column-revenue-label');
      if (!addedText1) return;
      const serializedText1 = new XMLSerializer().serializeToString(addedText1);
      const updatedSvg1 = svg1.replace('</svg>', `${serializedText1}</svg>`);

      // Get SVG for chart2 (without added text)

      const width1 = chart1.chartWidth;
      const width2 = chart2.chartWidth;
      const combinedHeight = Math.max(chart1.chartHeight, chart2.chartHeight);

      const leftPadding = 20;
      const rightPadding = 20;
      const dividerWidth = 2;
      const gapBetweenCharts = 15;

      const newCombinedWidth = leftPadding + width1 + dividerWidth + gapBetweenCharts + width2 + rightPadding;

      const combinedSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${newCombinedWidth}" height="${combinedHeight}">
  <g transform="translate(${leftPadding},0)">
    ${updatedSvg1}
    <rect x="${width1}" y="0" width="${dividerWidth}" height="${combinedHeight}" fill="#ebecf0" />
    <g transform="translate(${width1 + dividerWidth + gapBetweenCharts},0)">${svg2}</g>
  </g>
</svg>`;

      const capturedImage = await convertToSvgImage({
        ref: { current: combinedSvg },
        title: isMarket ? ChartKeys[PageType.EXPLORE].STACK_MARKET : ChartKeys[PageType.EXPLORE].STACK_RETAILER,
      });
      handleExportImage(
        capturedImage,
        exportChartKey(data, selectedAbsoluteType, selectedSaleType, isMarket, isSegment),
      );
    };

    const scheduleSvgExport = () => {
      setTimeout(async () => {
        await updateSvgAndExportImage();
      }, 300);
    };

    const initExportProcess = () => {
      setTimeout(() => {
        scheduleSvgExport();
      }, 500);
    };

    initExportProcess();
  }, []);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
    }
  }, []);

  useEffect(() => {
    if (isEmptyData) noResultHandler();
  }, [isEmptyData]);

  if (isEmptyData) return <NoResult />;

  return (
    <div data-test-id="explore-trends-stack-chart">
      <div className="flex w-full">
        <div className="flex-initial">
          <HighchartsReact
            ref={chartComponent1}
            highcharts={Highcharts}
            options={memoizedOptions(data.categories.slice(0, 2), 'data1', false, 0)}
            callback={callback1}
          />
        </div>
        <div className="flex-initial ml-5 mr-8">
          <div className="w-0.5 h-full bg-medium" />
        </div>
        <div
          ref={scrollContainerRef}
          className="flex-auto relative overflow-x-auto overflow-y-hidden"
          style={{ marginBottom: isSegment ? -15 : -28 }}
        >
          <div style={{ width: 90 * data.categories.slice(2).length + 20 }} className="absolute top-0 left-0">
            <div className="relative">
              <HighchartsReact
                ref={chartComponent2}
                highcharts={Highcharts}
                options={memoizedOptions(data.categories.slice(2), 'data2', true, data.categories.slice(0, 2).length)}
                callback={callback2}
              />
              <div className="absolute w-full opacity-0">
                <HighchartsReact
                  ref={chartComponent3}
                  highcharts={Highcharts}
                  options={memoizedOptions(data.categories.slice(2), 'data2', true, data.categories.slice(0, 2).length)}
                  callback={callback3}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreTrendsStackChart;
